import type { QueryClient } from '@tanstack/react-query'
import { contactFacetsKey } from '@/hooks/useContactFacets'
import type { ContactDetail } from '@/types/Contact'

/**
 * Chaves de cache do recurso **um contato**.
 *
 * Raiz `'contact'` (singular), disjunta de `'contacts'` (plural, a LISTAGEM).
 * Não é preferência de nome: `contactsKeys.all` é `['contacts']` e o TanStack
 * invalida por PREFIXO, então enquanto o detalhe morou sob `['contacts', …]` toda
 * invalidação de lista derrubava o detalhe junto — inclusive o que a mutação
 * acabava de escrever no `onSuccess`. Com raízes diferentes a colisão não é
 * evitada por disciplina: ela não existe.
 */
export const contactKeys = {
  /** Tudo de um contato — prefixo de todas abaixo. */
  todos: (id: string) => ['contact', id] as const,
  detail: (id: string) => ['contact', id, 'detail'] as const,
  protocols: (id: string) => ['contact', id, 'protocols'] as const,
  events: (id: string) => ['contact', id, 'events'] as const,
  /** Leituras da ficha (abas Resumo/Linha do tempo/Galeria). Sob `todos(id)` pela organização, mas
   *  `invalidaContato` as EXCLUI (`LEITURAS_DA_FICHA`): o PATCH não muda o que elas mostram. */
  summary: (id: string) => ['contact', id, 'summary'] as const,
  timeline: (id: string) => ['contact', id, 'timeline'] as const,
  /** Uma entrada por `kind` (`media|docs|links|contacts|templates|flows`). */
  gallery: (id: string, kind: string) => ['contact', id, 'gallery', kind] as const,
}

/**
 * Discriminadores (posição 2 da chave) das leituras da ficha. O que elas mostram — protocolos,
 * mensagens, mídia, fusões — não sai do `PATCH` de `name`/`email`/`address`, o único caminho de
 * `invalidaContato` hoje; revalidá-las refazia em série TODAS as páginas carregadas da leitura
 * ativa (infinite query), e o modal de edição só fecha quando a promessa assenta.
 */
const LEITURAS_DA_FICHA: ReadonlySet<unknown> = new Set(['summary', 'timeline', 'gallery'])

interface OpcoesDeInvalidacao {
  /**
   * Invalidar também o universo de país/DDD.
   *
   * `true` só para o que pode MUDAR esse universo: as facets saem de
   * `distinct_origin_pairs`, que casa `{'active': True, 'country_code': {'$ne': None}}`
   * (`back: contacts/services/repository.py`) e projeta país/DDD.
   * Ou seja: `active` (`delete`/`restore`) e o TELEFONE de um contato novo. O
   * `PATCH` NÃO entra — `PatchContactSchema` aceita `name`/`email`/`address` e mais
   * nada, e país/DDD são `@computed_field` derivados de `phone_number`.
   * `block` não toca nenhum dos dois.
   */
  facets?: boolean
}

/**
 * Revalida a seção **Contatos** da galeria de TODA ficha (`['contact', *, 'gallery', 'contacts']`).
 *
 * A linha resolvida dali lê o contato COMPARTILHADO de dentro da página da galeria
 * (`system_contacts`), keyada pelo id da FICHA: nenhuma chave carrega o id do contato que mudou,
 * então não há recorte cirúrgico — é o mesmo caso da listagem. Sem isto, bloquear/adicionar/o eco
 * do socket deixavam a linha com o estado velho e ofereciam bloquear/adicionar de novo (review A17).
 *
 * Só o kind `contacts`: é o único cujo item embute estado de OUTRO contato. As demais leituras da
 * ficha seguem fora (`LEITURAS_DA_FICHA`, ver `invalidaContato`).
 */
export function invalidaContatosDaGaleria(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'contact' && q.queryKey[2] === 'gallery' && q.queryKey[3] === 'contacts',
  })
}

/**
 * "Algo mudou nos contatos e o estado novo NÃO está em mãos" — revalida a
 * LISTAGEM, a seção Contatos da galeria das fichas (`invalidaContatosDaGaleria`) e,
 * opcionalmente, o universo de país/DDD.
 *
 * Separada de `invalidaContato` de propósito: quem acabou de gravar o
 * `ContactDetail` que o back devolveu (as mutações de estado) precisa **desta**,
 * e não daquela. Revalidar o namespace do contato ali seria refetchar o que se
 * acabou de escrever — e esse GET volta **403** pra quem mudou o contato pra um
 * estado que a própria permission esconde (`contacts:delete` sem
 * `contacts:deleted:read`): a operação deu certo e a tela diria que falhou.
 */
export function invalidaListagensDeContatos(
  queryClient: QueryClient,
  { facets = false }: OpcoesDeInvalidacao = {},
): Promise<void> {
  /* A listagem NÃO é keyada por id (`['contacts', {search|lookup, …}]`), então
     não dá pra ser cirúrgico: deletar precisa tirar o contato da vista de ativos.
     O predicado aqui separa LISTAGEM de FACETS — as duas moram sob `['contacts']`
     mas são recursos diferentes (`contactFacetsKey` é `['contacts','facets']`, com
     STRING na posição 1), e facet só muda com `active`. Isto NÃO é o remendo que o
     split veio substituir: aquele era predicado pra contornar colisão de
     namespace; este é recorte entre dois recursos que legitimamente compartilham
     raiz. */
  const pendentes = [
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === 'contacts' &&
        typeof q.queryKey[1] === 'object' &&
        q.queryKey[1] !== null,
    }),
    /* Única exceção ao "não toca o namespace do contato": a seção Contatos da galeria é, na
       prática, uma LISTAGEM de outros contatos (o estado vem embutido em `system_contacts`).
       O 403 que a regra evita é o do gate de ESTADO do detalhe; a galeria é gateada por
       permission (`contacts:gallery:read` + `protocols:messages:read`, `services/contacts/galeria`). */
    invalidaContatosDaGaleria(queryClient),
  ]
  if (facets) pendentes.push(queryClient.invalidateQueries({ queryKey: contactFacetsKey }))
  return Promise.all(pendentes).then(() => undefined)
}

/**
 * "Este contato mudou e eu NÃO tenho o estado novo": revalida o namespace do
 * contato (detail + protocols + events) **e** as listagens — MENOS as leituras da ficha
 * (summary/timeline/gallery, ver `LEITURAS_DA_FICHA`).
 *
 * É o caso do `PATCH` de campos (`patchContact` devolve `void` — não há o que
 * gravar, e o `PATCH` não muda ESTADO, então a re-leitura tem o mesmo veredito do
 * gate que a leitura que abriu o painel).
 *
 * **NÃO é o caso de nada que mude estado**: block, restore, delete e o eco de
 * `contact_state_changed` gravam o estado novo (`setQueryData`
 * direto ou `aplicaEstadoNoDetalhe`, ver [[./contactCacheKeys.md]]) e chamam
 * `invalidaListagensDeContatos`, porque o refetch daqui volta 403 pra quem pode
 * MUDAR e não pode LER aquele estado.
 */
export function invalidaContato(
  queryClient: QueryClient,
  contactId: string,
  { facets = false }: OpcoesDeInvalidacao = {},
): Promise<void> {
  // Prefixo: pega o namespace daquele contato, e de nenhum outro; o predicado (que o TanStack
  // aplica DEPOIS do prefixo, `matchQuery` em query-core/utils) tira as leituras da ficha.
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: contactKeys.todos(contactId),
      predicate: (q) => !LEITURAS_DA_FICHA.has(q.queryKey[2]),
    }),
    invalidaListagensDeContatos(queryClient, { facets }),
  ]).then(() => undefined)
}

/**
 * Escreve no cache do detalhe o estado que JÁ se conhece, sem ida ao servidor.
 *
 * Terceira ocorrência do mesmo idioma (block, delete, eco do socket), e o motivo é
 * sempre o mesmo: **refetchar o detalhe depois de mudar o estado dele volta 403**
 * pra quem tem a permission de MUDAR e não a de LER aquele estado
 * (`contacts:block` sem `blocked:read`, `contacts:delete` sem `deleted:read` —
 * esta última fora de TODO preset).
 *
 * Só patcha entrada QUE JÁ EXISTE: sem ela o painel não está aberto, e montar um
 * `ContactDetail` a partir de um payload magro inventaria campos que ninguém leu.
 */
export function aplicaEstadoNoDetalhe(
  queryClient: QueryClient,
  contactId: string,
  estado: Partial<Pick<ContactDetail, 'blocked' | 'active'>>,
): void {
  const anterior = queryClient.getQueryData<ContactDetail>(contactKeys.detail(contactId))
  if (!anterior) return
  queryClient.setQueryData(contactKeys.detail(contactId), { ...anterior, ...estado })
}

/**
 * "Este contato mudou de estado E eu POSSO ver o estado novo": revalida **só o
 * detalhe**.
 *
 * Não é `invalidaContato`: o namespace `['contact', id]` arrastaria `events`, que
 * passa por OUTRA permission (`contacts:history:read`, fora de todo preset do back) —
 * refetchar aquilo é um 403 novo em troca de nada. O que precisa revalidar é
 * `blocked_on`, e ele mora no detalhe.
 *
 * Quem decide o "eu posso ver" é o chamador, com o estado que o próprio payload do
 * evento carrega — nunca chamando o back pra descobrir.
 */
export function revalidaDetalheDoContato(
  queryClient: QueryClient,
  contactId: string,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) })
}
